import { useMemo, type ReactNode } from 'react';

// eslint-disable-next-line @typescript-eslint/no-deprecated
import type { FormControlState } from '../types/form-control-state.js';

/**
 * Array tuple informing whether the form control state features errors or not and
 * an optional React node element informing about error details.
 * @internal
 */
export type useFormControlStateReturnType = [
  /** Boolean flag that informs if the input control is in error state.  */
  boolean,
  /** Error hint details, if any, as retrieved from the `FormControlState` value. */
  ReactNode,
];

/**
 * Convenience hook for reading the `FormControlState` details of
 * a form control implementing the `FormControlStateProps` interface.
 * @internal
 */
export function useFormControlState(
  /** Value for the form control state property derived from the `FormControlStateProps` interface. */
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  formControlState?: FormControlState,
): useFormControlStateReturnType {
  const [isError, hint] = useMemo(() => {
    const controlState =
      formControlState && typeof formControlState === 'string'
        ? { state: formControlState }
        : formControlState;

    return [controlState?.state === 'error', controlState?.hint];
  }, [formControlState]);

  return [isError, hint];
}
